// qc_report.js
import React, { useState, useEffect, useRef } from "react";
import {
  Download,
  Calendar,
  Filter,
  BarChart3,
  FileText,
  Loader2,
} from "lucide-react";
import API_URL from "../../src/Config";
import "./QCReportsPage.css"; // <<< add this import

const QCReportsPage = ({ isSidebarExpanded }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [availableYears, setAvailableYears] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("summary");
  const [filters, setFilters] = useState({
    start_date: "",
    end_date: "",
    rating: "",
    section_code: "",
    group_period: "day",
  });
  const containerRef = useRef(null);

  const getAuthToken = () => {
    return localStorage.getItem("authKey") || "";
  };

  const apiHeaders = {
    Authorization: getAuthToken(),
    "Content-Type": "application/json",
  };

  useEffect(() => {
    fetchAvailableYears();
  }, []);

  useEffect(() => {
    if (selectedYear) {
      if (activeTab === "summary") {
        fetchSummaryData();
      } else {
        fetchDetailedData();
      }
    }
  }, [selectedYear, activeTab, filters]);

  const fetchAvailableYears = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/qc/reports/available-years`,
        {
          headers: apiHeaders,
        }
      );
      const data = await response.json();

      if (response.ok) {
        setAvailableYears(data.available_years);
        setSelectedYear(data.current_year);
      } else {
        console.error("Error fetching years:", data.error);
      }
    } catch (error) {
      console.error("Error fetching available years:", error);
    }
  };

  const fetchSummaryData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        cycle_year: selectedYear,
        group_period: filters.group_period,
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
      });

      const response = await fetch(
        `${API_URL}/api/qc/reports/summary?${queryParams}`,
        {
          headers: apiHeaders,
        }
      );
      const data = await response.json();

      if (response.ok) {
        setSummaryData(data);
      } else {
        console.error("Error fetching summary:", data.error);
      }
    } catch (error) {
      console.error("Error fetching summary data:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDetailedData = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        cycle_year: selectedYear,
        page: 1,
        per_page: 100,
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.rating && { rating: filters.rating }),
        ...(filters.section_code && { section_code: filters.section_code }),
      });

      const response = await fetch(
        `${API_URL}/api/qc/reports/detailed?${queryParams}`,
        {
          headers: apiHeaders,
        }
      );
      const data = await response.json();

      if (response.ok) {
        setReportData(data);
      } else {
        console.error("Error fetching detailed report:", data.error);
      }
    } catch (error) {
      console.error("Error fetching detailed data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    setExportLoading(true);
    try {
      const queryParams = new URLSearchParams({
        cycle_year: selectedYear,
        format: format,
        ...(filters.start_date && { start_date: filters.start_date }),
        ...(filters.end_date && { end_date: filters.end_date }),
        ...(filters.rating && { rating: filters.rating }),
        ...(filters.section_code && { section_code: filters.section_code }),
      });

      const response = await fetch(
        `${API_URL}/api/qc/reports/export?${queryParams}`,
        {
          headers: {
            Authorization: getAuthToken(),
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.style.display = "none";
        a.href = url;
        a.download = `QC_Report_${selectedYear}.${format}`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        const errorData = await response.json();
        console.error("Export error:", errorData.error);
        alert("Export failed: " + errorData.error);
      }
    } catch (error) {
      console.error("Export error:", error);
      alert("Export failed. Please try again.");
    } finally {
      setExportLoading(false);
    }
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const clearFilters = () => {
    setFilters({
      start_date: "",
      end_date: "",
      rating: "",
      section_code: "",
      group_period: "day",
    });
  };

  const formatPercentage = (value, total) => {
    return total > 0 ? ((value / total) * 100).toFixed(1) + "%" : "0%";
  };

  const StatCard = ({ title, value, percentage, tone = "primary" }) => (
    <div className={`qc-card qc-stat qc-stat--${tone}`}>
      <div className="qc-stat__body">
        <p className="qc-stat__label">{title}</p>
        <p className="qc-stat__value">{value}</p>
        <p className="qc-stat__meta">{percentage}</p>
      </div>
    </div>
  );

  return (
    <>
      <div
        className={`data-list-content-container page-content ${
          isSidebarExpanded ? "expanded" : "collapsed"
        }`}
      >
        <div className="data-form-content data-list-content" ref={containerRef}>
          <div className="qc-container data-list-header">
            {/* Header */}
            <div className="qc-header data-list-header-content">
              <div>
                <h1 className="qc-title data-list-heading-content-left-side">
                  QC Reports
                </h1>
                <p className="qc-subtitle">
                  Quality Control inspection reports and analytics
                </p>
              </div>

              <div className="qc-header__actions">
                {/* Year Selector */}
                <div className="qc-year">
                  {/* <Calendar className="qc-icon-calendar" /> */}
                  <select
                    value={selectedYear}
                    onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                    className="qc-select"
                  >
                    {availableYears.map((year) => (
                      <option key={year} value={year}>
                        {year}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Export Buttons */}
                <div className="qc-export">
                  <button
                    onClick={() => handleExport("csv")}
                    disabled={exportLoading}
                    className="qc-btn qc-btn--success"
                  >
                    {exportLoading ? (
                      <Loader2 className="qc-icon qc-icon--spin" />
                    ) : (
                      <Download className="qc-icon" />
                    )}
                    CSV
                  </button>
                  <button
                    onClick={() => handleExport("xlsx")}
                    disabled={exportLoading}
                    className="qc-btn qc-btn--primary"
                  >
                    {exportLoading ? (
                      <Loader2 className="qc-icon qc-icon--spin" />
                    ) : (
                      <Download className="qc-icon" />
                    )}
                    Excel
                  </button>
                </div>
              </div>
            </div>

            <div className="data-list-body data-list-body-qc">
              <div className="data-list-scroll-body-iqcm">
                <div className="data-list-table">
                  {/* Tabs */}
                  <div className="qc-card qc-tabs">
                    <div className="qc-tabs__list">
                      <button
                        onClick={() => setActiveTab("summary")}
                        className={`qc-tab ${
                          activeTab === "summary" ? "is-active" : ""
                        }`}
                      >
                        <BarChart3 className="qc-icon" />
                        <span>Summary Report</span>
                      </button>
                      <button
                        onClick={() => setActiveTab("detailed")}
                        className={`qc-tab ${
                          activeTab === "detailed" ? "is-active" : ""
                        }`}
                      >
                        <FileText className="qc-icon" />
                        <span>Detailed Report</span>
                      </button>
                    </div>
                  </div>

                  {/* Filters Panel */}

                  

                  <div className="qc-card qc-filters">
                    <div className="qc_dflex">
                      <div className="qc-filters__header">
                        <Filter className="qc-icon" />
                        <span className="qc-filters__title">Filters</span>
                      </div>

                      <div className="qc-filters__actions">
                        <button
                          onClick={clearFilters}
                          className="qc-btn qc-btn--ghost"
                        >
                          Clear Filters
                        </button>
                      </div>
                    </div>

                    <div
                    className={`qc-filters__fields ${
                      activeTab === "summary"
                        ? "qc-filters--summary"
                        : "qc-filters--detailed"
                    }`}
                  >
                    <div className="qc-field">
                      <label className="qc-label">Start Date</label>
                      <input
                        type="date"
                        value={filters.start_date}
                        onChange={(e) =>
                          handleFilterChange("start_date", e.target.value)
                        }
                        className="qc-input"
                      />
                    </div>

                    <div className="qc-field">
                      <label className="qc-label">End Date</label>
                      <input
                        type="date"
                        value={filters.end_date}
                        onChange={(e) =>
                          handleFilterChange("end_date", e.target.value)
                        }
                        className="qc-input"
                      />
                    </div>

                    {activeTab === "detailed" && (
                      <div className="qc-field">
                        <label className="qc-label">Rating</label>
                        <select
                          value={filters.rating}
                          onChange={(e) =>
                            handleFilterChange("rating", e.target.value)
                          }
                          className="qc-select"
                        >
                          <option value="">All Ratings</option>
                          <option value="FM">FM</option>
                          <option value="PM">PM</option>
                          <option value="NC">NC</option>
                          <option value="NA">NA</option>
                          <option value="NYS">NYS</option>
                        </select>
                      </div>
                    )}

                    {activeTab === "detailed" && (
                      <div className="qc-field">
                        <label className="qc-label">Section Code</label>
                        <input
                          type="text"
                          value={filters.section_code}
                          onChange={(e) =>
                            handleFilterChange("section_code", e.target.value)
                          }
                          placeholder="Section code"
                          className="qc-input"
                        />
                      </div>
                    )}

                    {activeTab === "summary" && (
                      <div className="qc-field">
                        <label className="qc-label">Group By</label>
                        <select
                          value={filters.group_period}
                          onChange={(e) =>
                            handleFilterChange("group_period", e.target.value)
                          }
                          className="qc-select"
                        >
                          <option value="day">Day</option>
                          <option value="week">Week</option>
                          <option value="month">Month</option>
                        </select>
                      </div>
                    )}
                  </div>

                    {/* <div className="qc-grid">
                      <div className="qc-field">
                        <label className="qc-label">Start Date</label>
                        <input
                          type="date"
                          value={filters.start_date}
                          onChange={(e) =>
                            handleFilterChange("start_date", e.target.value)
                          }
                          className="qc-input"
                        />
                      </div>

                      <div className="qc-field">
                        <label className="qc-label">End Date</label>
                        <input
                          type="date"
                          value={filters.end_date}
                          onChange={(e) =>
                            handleFilterChange("end_date", e.target.value)
                          }
                          className="qc-input"
                        />
                      </div>
                      {activeTab === "detailed" && (
                        <div className="qc-field">
                          <label className="qc-label">Rating</label>
                          <select
                            value={filters.rating}
                            onChange={(e) =>
                              handleFilterChange("rating", e.target.value)
                            }
                            className="qc-select"
                          >
                            <option value="">All Ratings</option>
                            <option value="FM">FM</option>
                            <option value="PM">PM</option>
                            <option value="NC">NC</option>
                            <option value="NA">NA</option>
                            <option value="NYS">NYS</option>
                          </select>
                        </div>
                      )}
                      {activeTab === "detailed" && (
                        <div className="qc-field">
                          <label className="qc-label">Section Code</label>
                          <input
                            type="text"
                            value={filters.section_code}
                            onChange={(e) =>
                              handleFilterChange("section_code", e.target.value)
                            }
                            placeholder="Section code"
                            className="qc-input"
                          />
                        </div>
                      )}
                      {activeTab === "summary" && (
                        <div className="qc-field">
                          <label className="qc-label">Group By</label>
                          <select
                            value={filters.group_period}
                            onChange={(e) =>
                              handleFilterChange("group_period", e.target.value)
                            }
                            className="qc-select"
                          >
                            <option value="day">Day</option>
                            <option value="week">Week</option>
                            <option value="month">Month</option>
                          </select>
                        </div>
                      )}
                    </div> */}
                  </div>

                  {/* Loading State */}
                  {loading && (
                    <div className="qc-loading">
                      <Loader2 className="qc-icon qc-icon--spin qc-icon--lg qc-text-primary" />
                      <span className="qc-loading__text">
                        Loading report data...
                      </span>
                    </div>
                  )}

                  {/* Summary Tab */}
                  {activeTab === "summary" && summaryData && !loading && (
                    <div className="qc-stack">
                      {/* Overall Statistics */}
                      <div>
                        <h2 className="qc-section-title">Overall Statistics</h2>
                        <div className="qc-grid qc-grid--stats">
                          <StatCard
                            title="Total Inspections"
                            value={
                              summaryData.overall_statistics
                                ?.total_inspections || 0
                            }
                            percentage={`${
                              summaryData.overall_statistics?.inspection_days ||
                              0
                            } days`}
                            tone="primary"
                          />
                          <StatCard
                            title="FM (Fully Met)"
                            value={
                              summaryData.overall_statistics?.fm_count || 0
                            }
                            percentage={`${
                              summaryData.overall_statistics?.fm_percentage ?? 0
                            }%`}
                            tone="success"
                          />
                          <StatCard
                            title="PM (Partially Met)"
                            value={
                              summaryData.overall_statistics?.pm_count || 0
                            }
                            percentage={`${
                              summaryData.overall_statistics?.pm_percentage ?? 0
                            }%`}
                            tone="warning"
                          />
                          <StatCard
                            title="NC (Not Complied)"
                            value={
                              summaryData.overall_statistics?.nc_count || 0
                            }
                            percentage={`${
                              summaryData.overall_statistics?.nc_percentage ?? 0
                            }%`}
                            tone="danger"
                          />
                          <StatCard
                            title="Not Applicable"
                            value={
                              (summaryData.overall_statistics?.na_count || 0) +
                              (summaryData.overall_statistics?.nys_count || 0)
                            }
                            percentage={`${
                              (summaryData.overall_statistics?.na_percentage ||
                                0) +
                              (summaryData.overall_statistics?.nys_percentage ||
                                0)
                            }%`}
                            tone="muted"
                          />
                        </div>
                      </div>

                      {/* Section Breakdown */}
                      {summaryData.section_breakdown &&
                        summaryData.section_breakdown.length > 0 && (
                          <div>
                            <h2 className="qc-section-title">
                              Section-wise Performance
                            </h2>
                            <div className="qc-card qc-table-card">
                              <table className="qc-table table multilevel-data">
                                <thead>
                                  <tr>
                                    <th>Section</th>
                                    <th>Total</th>
                                    <th className="qc-text-success">FM</th>
                                    <th className="qc-text-warning">PM</th>
                                    <th className="qc-text-danger">NC</th>
                                    <th className="qc-text-muted">NA/NYS</th>
                                  </tr>
                                </thead>
                                <tbody className="data-list-scroll-bodys">
                                  {summaryData.section_breakdown.map(
                                    (section, index) => (
                                      <tr key={index} className="hoverable-row">
                                        <td>
                                          <div className="qc-cell-title">
                                            {section.section_code}
                                          </div>
                                          <div className="qc-cell-subtitle">
                                            {section.section_title}
                                          </div>
                                        </td>
                                        <td>{section.total_answers}</td>
                                        <td className="qc-text-success">
                                          {section.fm_count} (
                                          {section.fm_percentage}
                                          %)
                                        </td>
                                        <td className="qc-text-warning">
                                          {section.pm_count} (
                                          {section.pm_percentage}
                                          %)
                                        </td>
                                        <td className="qc-text-danger">
                                          {section.nc_count} (
                                          {section.nc_percentage}
                                          %)
                                        </td>
                                        <td className="qc-text-muted">
                                          {section.na_count + section.nys_count}{" "}
                                          (
                                          {(
                                            (Number(section.na_percentage) ||
                                              0) +
                                            (Number(section.nys_percentage) ||
                                              0)
                                          ).toFixed(1)}
                                          %)
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}

                      {/* Inspector Performance */}
                      {summaryData.inspector_performance &&
                        summaryData.inspector_performance.length > 0 && (
                          <div>
                            <h2 className="qc-section-title">
                              Inspector Performance
                            </h2>
                            <div className="qc-card qc-table-card">
                              <table className="qc-table table multilevel-data">
                                <thead>
                                  <tr>
                                    <th>Inspector</th>
                                    <th>Total Inspections</th>
                                    <th className="qc-text-success">FM Rate</th>
                                    <th className="qc-text-danger">NC Rate</th>
                                    <th>Last Inspection</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {summaryData.inspector_performance.map(
                                    (inspector, index) => (
                                      <tr key={index}>
                                        <td className="qc-strong">
                                          {inspector.inspector || "Unknown"}
                                        </td>
                                        <td>{inspector.total_inspections}</td>
                                        <td className="qc-text-success">
                                          {inspector.fm_percentage}%
                                        </td>
                                        <td className="qc-text-danger">
                                          {inspector.nc_percentage}%
                                        </td>
                                        <td className="qc-text-muted">
                                          {inspector.last_inspection}
                                        </td>
                                      </tr>
                                    )
                                  )}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                    </div>
                  )}

                  {/* Detailed Tab */}
                  {activeTab === "detailed" && reportData && !loading && (
                    <div className="qc-stack">
                      {/* Summary Stats */}
                      {reportData.summary && (
                        <div>
                          <h2 className="qc-section-title">
                            Summary Statistics
                          </h2>
                          <div className="qc-grid qc-grid--stats">
                            <StatCard
                              title="Total Records"
                              value={reportData.summary.total_answers || 0}
                              percentage={`${
                                reportData.summary.unique_inspectors || 0
                              } inspectors`}
                              tone="primary"
                            />
                            <StatCard
                              title="FM (Fully Met)"
                              value={reportData.summary.fm_count || 0}
                              percentage={formatPercentage(
                                reportData.summary.fm_count,
                                reportData.summary.total_answers
                              )}
                              tone="success"
                            />
                            <StatCard
                              title="PM (Partially Met)"
                              value={reportData.summary.pm_count || 0}
                              percentage={formatPercentage(
                                reportData.summary.pm_count,
                                reportData.summary.total_answers
                              )}
                              tone="warning"
                            />
                            <StatCard
                              title="NC (Not Complied)"
                              value={reportData.summary.nc_count || 0}
                              percentage={formatPercentage(
                                reportData.summary.nc_count,
                                reportData.summary.total_answers
                              )}
                              tone="danger"
                            />
                            <StatCard
                              title="NA/NYS (Not Applicable / Not Yet Started)"
                              value={
                                (reportData.summary.na_count || 0) +
                                (reportData.summary.nys_count || 0)
                              }
                              percentage={formatPercentage(
                                (reportData.summary.na_count || 0) +
                                  (reportData.summary.nys_count || 0),
                                reportData.summary.total_answers
                              )}
                              tone="muted"
                            />
                          </div>
                        </div>
                      )}

                      {/* Detailed Records */}
                      {reportData.records && reportData.records.length > 0 && (
                        <div>
                          <div className="qc-list-header">
                            <h2 className="qc-section-title">
                              Detailed Records
                            </h2>
                            <span className="qc-muted">
                              Showing {reportData.records.length} of{" "}
                              {reportData.pagination?.total_records || 0}{" "}
                              records
                            </span>
                          </div>

                          <div className="qc-card qc-table-card qc-table-card--scroll">
                            <div className="qc-table-wrap">
                              <table className="qc-table table multilevel-data">
                                <thead>
                                  <tr>
                                    <th>Section</th>
                                    <th>Question</th>
                                    <th>Rating</th>
                                    <th>Inspector</th>
                                    <th>Inspected On</th>
                                    <th>Remarks</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {reportData.records.map((record, index) => (
                                    <tr key={index}>
                                      <td>
                                        <div className="qc-cell-title">
                                          {record.section_code}
                                        </div>
                                        <div className="qc-cell-subtitle qc-ellipsis">
                                          {record.section_title}
                                        </div>
                                      </td>
                                      <td>
                                        <div className="qc-cell-title">
                                          {record.question_code}
                                        </div>
                                        <div className="qc-cell-subtitle">
                                          {record.prompt}
                                        </div>
                                      </td>
                                      <td>
                                        <span
                                          className={
                                            `qc-badge ` +
                                            (record.rating === "FM"
                                              ? "qc-badge--success"
                                              : record.rating === "PM"
                                              ? "qc-badge--warning"
                                              : record.rating === "NC"
                                              ? "qc-badge--danger"
                                              : "qc-badge--muted")
                                          }
                                        >
                                          {record.rating}
                                        </span>
                                      </td>
                                      <td>{record.inspector || "-"}</td>
                                      <td className="qc-text-muted">
                                        {record.inspected_on}
                                      </td>
                                      <td className="qc-ellipsis">
                                        {record.remarks || "-"}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* No Data */}
                  {!loading &&
                    ((activeTab === "summary" && !summaryData) ||
                      (activeTab === "detailed" && !reportData)) && (
                      <div className="qc-empty">
                        <div className="qc-empty__content">
                          <BarChart3 className="qc-icon qc-icon--xl qc-empty__icon" />
                          <h3 className="qc-empty__title">No data available</h3>
                          <p className="qc-empty__text">
                            No inspection data found for the selected year and
                            filters.
                          </p>
                        </div>
                      </div>
                    )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QCReportsPage;
